import type { Variants } from 'motion/react';

import type { Direction } from '@/i18n/languages';
import { enterTransition, exitTransition, helloTransition, popSpring, RISE, SLIDE, STAGGER } from './tokens';

/**
 * One vocabulary for the whole flow. Every onboarding element is either
 * `hidden` — before it arrives and after it leaves — or `shown`, and a panel
 * that is being replaced is `gone`. Parents animate the labels; children only
 * declare what each label means for them, which is why nothing in the flow
 * counts indices or measures anything.
 */

/** The default entrance: fade and rise in place. */
export const riseVariants: Variants = {
  hidden: { opacity: 0, y: RISE },
  shown: { opacity: 1, y: 0, transition: enterTransition },
};

/**
 * A panel inside a `StepStage` — either a whole screen's content arriving in
 * the onboarding flow's persistent card, or one question replacing another
 * inside a screen.
 *
 * `mode` is the difference between the stage's *first* panel, which arrives
 * with the screen it is part of and so rises like everything else on it, and a
 * panel that *replaces* another: that one travels horizontally and the
 * outgoing panel leaves the way the incoming one came from, so two panels read
 * as one strip moving under a window.
 *
 * `movement` is which way that strip moves. Forward brings a panel in from the
 * trailing edge and back brings it in from the leading edge — the same
 * direction a native stack pushes and pops, so the flow has a direction you can
 * feel on a phone. `travel` stays a transform, so a panel that fills the card
 * costs nothing to move.
 */
export function panelVariants({
  direction,
  mode,
  movement = 'forward',
  travel = SLIDE,
}: {
  direction: Direction;
  mode: 'rise' | 'slide';
  movement?: 'forward' | 'backward';
  travel?: number;
}): Variants {
  const reading = direction === 'rtl' ? -travel : travel;
  const from = movement === 'forward' ? reading : -reading;

  const x = mode === 'slide' ? from : 0;
  const y = mode === 'slide' ? 0 : RISE;

  return {
    hidden: { opacity: 0, x, y },
    shown: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: {
        ...enterTransition,
        // On mount the panel waits out the step counter above it; on a swap it
        // is the only thing moving, so it starts at once.
        delay: mode === 'rise' ? STAGGER : 0,
        delayChildren: STAGGER,
        staggerChildren: STAGGER,
      },
    },
    gone: { opacity: 0, x: -from, y: 0, transition: exitTransition },
  };
}

/** The tick badge: a small pop on the tap that set it, and out again. */
export const tickVariants: Variants = {
  hidden: { opacity: 0, scale: 0.5 },
  shown: { opacity: 1, scale: 1, transition: popSpring },
};

/** The buddy's reaction to being chosen. `idle` is deliberately empty. */
export const helloVariants: Variants = {
  idle: {},
  hello: {
    y: ['0%', '-8%', '0%', '0%'],
    scale: [1, 1.07, 1.02, 1],
    rotate: [0, -2.5, 1, 0],
    transition: helloTransition,
  },
};

/** Spread on a child of a staged card or panel: it inherits the label. */
export const stagedChild = { variants: riseVariants } as const;

/** Press feedback for a tappable surface, paired with `pressSpring`. */
export const pressProps = { whileTap: { scale: 0.98 } } as const;

/* ---------------------------------------------------------------------------
   Journey and reward screens — ticket 16 additions. Same vocabulary as above:
   a parent animates `hidden` → `shown` and its children only declare what the
   labels mean for them.
   --------------------------------------------------------------------------- */

/**
 * A journey screen's content column. It arrives as one block — there is no
 * card being pushed onto a stack here, the screen *is* the destination — and
 * then hands the beat to its children in DOM order, so the greeting reads
 * before the trail does.
 *
 * `staggerChildren` on the column rather than a delay per card: the trail is
 * three or four cards for one learner and the stagger has to stay the same
 * regardless, which counting indices in the component would not achieve.
 */
export const columnVariants: Variants = {
  hidden: {},
  shown: { transition: { delayChildren: STAGGER, staggerChildren: STAGGER * 1.6 } },
};

/**
 * One stop on the trail. It rises like everything else, and it is `layout` at
 * the call site so that a module changing state — a card losing its yellow
 * halo and its neighbour gaining one — settles rather than jumps.
 */
export const trailStopVariants: Variants = {
  hidden: { opacity: 0, y: RISE },
  shown: { opacity: 1, y: 0, transition: enterTransition },
};

/**
 * The tick that lands on a module the learner has just finished. A spring with
 * one overshoot, because a completion is the screen's one moment worth
 * celebrating; everything else on the journey is critically damped.
 */
export const trailTickVariants: Variants = {
  hidden: { opacity: 0, scale: 0.4 },
  shown: { opacity: 1, scale: 1, transition: popSpring },
};

/** Spread on a child of a journey column: it inherits the parent's label. */
export const columnChild = { variants: trailStopVariants } as const;
